// Port of Flutter's userScreen.dart — the "Me" tab, titled "Account Controls".
//
// A single column of rounded ButtonColor tiles, capped at 480 wide and centred.
// Two of them are gated on flags from the user record, exactly as in the Dart:
//   AllowTesting     == 1 -> "Test Device"   (open any device by Unique Number)
//   AllowTestingTuya == 1 -> "Scan Devices"
//
// The Dart's "Sync now (local DB)" tile is here too, master users only. It backs
// onto the SQLite offline cache (core/db/*) and reports row counts, which is the
// only way to tell the cache actually filled — a working cache and a working
// network look identical from the UI.

import React, { useEffect, useState } from 'react';
import {
  Box, ListItemButton, ListItemText, Avatar, useTheme, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
} from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DocumentScannerSharpIcon from '@mui/icons-material/DocumentScannerSharp';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import BluetoothSearchingIcon from '@mui/icons-material/BluetoothSearching';
import ForumIcon from '@mui/icons-material/Forum';
import ScienceIcon from '@mui/icons-material/Science';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import InfoIcon from '@mui/icons-material/Info';
import SyncIcon from '@mui/icons-material/Sync';
import WifiIcon from '@mui/icons-material/Wifi';
import { useNavigate } from 'react-router-dom';
import { CenterTitleBar } from '../../components/MobileAppBar';
import { useApp } from '../../../../core/state/AppContext';
import { useMessage } from '../../components/MessageDialog';
import { fullSync, tableCounts, lastSyncedAt } from '../../../../core/db/syncService';
import { dbSupported } from '../../../../core/db/schema';
import {
  isManualOffline, setManual, hasNoNetwork, onOfflineChange,
} from '../../../../core/offline/offlineMode';
import { isKiosk } from '../../../../core/wall/kioskMode';
import { APP_VERSION } from '../../../../core/version';
import AppUpdateDialog from '../../components/AppUpdateDialog';

function Tile({ leading, title, subtitle, onClick }) {
  const theme = useTheme();
  const c = theme.sli;
  return (
    <ListItemButton
      onClick={onClick}
      sx={{ bgcolor: c.button, borderRadius: '16px', p: 2, mb: 1.25, gap: 2 }}
    >
      {leading}
      <ListItemText
        primary={title}
        secondary={subtitle}
        primaryTypographyProps={{ sx: { color: c.text, fontSize: 18 } }}
        secondaryTypographyProps={{ sx: { color: `${c.text}B3`, fontSize: 13 } }}
      />
    </ListItemButton>
  );
}

export default function MeTab() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { user, profileUrl, isSuperAdmin, canTuyaTest } = useApp();

  const [testOpen, setTestOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  // Read on each render rather than held in state: the setting is changed on
  // the Wall Panel screen, so a copy taken here would go stale the moment the
  // user came back.
  const kioskOn = isKiosk();
  const [uno, setUno] = useState('');
  const [syncing, setSyncing] = useState(false);
  const { show } = useMessage();

  const [manualOffline, setManualOffline] = useState(() => isManualOffline());
  const [noNetwork, setNoNetwork] = useState(() => hasNoNetwork());
  // Connectivity can change while this tab is open, so the subtitle follows it.
  useEffect(() => onOfflineChange(() => {
    setManualOffline(isManualOffline());
    setNoNetwork(hasNoNetwork());
  }), []);

  const runSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const ok = await fullSync(Number(user?.UserId));
      const counts = await tableCounts();
      const at = await lastSyncedAt();
      const lines = Object.entries(counts).map(([t, n]) => `${t}: ${n}`).join('\n');
      show(
        ok ? 'Sync Complete' : 'Sync Finished (with issues)',
        `${lines}\n\nLast sync: ${at || '-'}`,
      );
    } finally {
      setSyncing(false);
    }
  };

  const masterUser = Number(user?.AllowTesting) === 1;
  const tuyaUser = Number(user?.AllowTestingTuya) === 1;

  const iconAvatar = (icon) => (
    <Avatar sx={{ bgcolor: c.back, color: c.text, width: 40, height: 40 }}>{icon}</Avatar>
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: c.back }}>
      <CenterTitleBar title="Account Controls" />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1.875 }}>
        <Box sx={{ maxWidth: 480, mx: 'auto' }}>
          <Tile
            leading={<Avatar src={profileUrl || undefined} sx={{ width: 40, height: 40 }} />}
            title="User Profile"
            onClick={() => navigate('/profile')}
          />

          {/* ONE entry — the setup page carries the grid, the on/off switch
              and a button that opens the panel. Three separate tiles were three
              names for one feature. */}
          <Tile
            leading={iconAvatar(<DashboardIcon />)}
            title="Wall Panel"
            subtitle={kioskOn ? 'On — this tablet opens on its panel' : 'Design it, or use this tablet as one'}
            onClick={() => navigate('/wall/setup')}
          />

          {masterUser && (
            <Tile
              leading={iconAvatar(<AdminPanelSettingsIcon />)}
              title="Test Device"
              onClick={() => { setUno(''); setTestOpen(true); }}
            />
          )}

          {/* Flutter's "Sync now (local DB)" — forces a full pull and reports
              row counts. Without it there is no way to tell whether the offline
              cache actually filled, since a working cache looks exactly like a
              working network. Master users only. */}
          {masterUser && dbSupported() && (
            <Tile
              leading={iconAvatar(<SyncIcon />)}
              title="Sync now (local DB)"
              subtitle={syncing ? 'Syncing…' : 'Refresh the offline cache'}
              onClick={runSync}
            />
          )}

          {tuyaUser && (
            <Tile
              leading={iconAvatar(<DocumentScannerSharpIcon />)}
              title="Scan Devices"
              onClick={() => navigate('/add-device?tuya=1')}
            />
          )}

          {/* Offline Mode — the Dart's manual toggle. On, the app serves
              everything from the local cache and sends commands over Bluetooth
              instead of the internet. Useful on a bad connection, and for
              cutting data use. It also turns itself on when there is genuinely
              no network, which is why the subtitle reports the effective state
              rather than just the switch. */}
          <ListItemButton
            onClick={() => { setManual(!manualOffline); setManualOffline(!manualOffline); }}
            sx={{ bgcolor: c.button, borderRadius: '16px', p: 2, mb: 1.25, gap: 2 }}
          >
            {iconAvatar(<CloudOffIcon />)}
            <ListItemText
              primary="Offline Mode"
              secondary={
                // eslint-disable-next-line no-nested-ternary
                manualOffline
                  ? 'On — using local data and Bluetooth'
                  : (noNetwork ? 'Auto — no network detected' : 'Off')
              }
              primaryTypographyProps={{ sx: { color: c.text, fontSize: 18 } }}
              secondaryTypographyProps={{ sx: { color: `${c.text}B3`, fontSize: 13 } }}
            />
            <Switch
              checked={manualOffline}
              onChange={(e) => { setManual(e.target.checked); setManualOffline(e.target.checked); }}
              onClick={(e) => e.stopPropagation()}
            />
          </ListItemButton>

          {/* Onboarding a new device is easiest to find here, not buried in a
              room FAB — it is the first thing done with hardware out of the box. */}
          <Tile
            leading={iconAvatar(<WifiIcon />)}
            title="Set Up New Device"
            subtitle="Find a new device and give it your Wi-Fi"
            onClick={() => navigate('/wifi-setup')}
          />

          {/* Bring-up tool, not a user feature: it shows the raw BLE exchange
              and is only meaningful to someone debugging firmware. */}
          {isSuperAdmin && (
            <Tile
              leading={iconAvatar(<ForumIcon />)}
              title="Message Box"
              subtitle="Live MQTT traffic, and send commands"
              onClick={() => navigate('/mqtt')}
            />
          )}

          {isSuperAdmin && (
            <Tile
              leading={iconAvatar(<BluetoothSearchingIcon />)}
              title="BLE Test"
              subtitle="Scan, authenticate and watch the raw exchange"
              onClick={() => navigate('/ble-test')}
            />
          )}

          {/* The production bench: the last devices seen on the broker, no
              account or mapping needed. Not a customer feature — it can rewrite
              a device's type and reboot it.
              Gated on `canTuyaTest`, NOT on isSuperAdmin: the permission the
              backend actually records is the Users flag AllowTestingTuya, which
              is how the bench operators are granted it and how Flutter gated the
              same page. */}
          {canTuyaTest && (
            <Tile
              leading={iconAvatar(<ScienceIcon />)}
              title="Tuya Testing"
              subtitle="Recently seen devices — control, set type, add Wi-Fi"
              onClick={() => navigate('/tuya-testing')}
            />
          )}

          {/* Estate-wide, and the load is expensive — so it is a place you go
              deliberately, not something the app does on its own. */}
          {isSuperAdmin && (
            <Tile
              leading={iconAvatar(<SystemUpdateAltIcon />)}
              title="Firmware"
              subtitle="Every device — which need updating, and update them"
              onClick={() => navigate('/firmware')}
            />
          )}

          <Tile
            leading={iconAvatar(<QrCodeScannerIcon />)}
            title="Add Shared Device"
            subtitle="Scan a device's share QR to add it"
            onClick={() => navigate('/scan-share')}
          />

          {/* Every user, not just admins: this is the only surface that can
              tell "you are up to date" apart from "the update server is
              broken", and the two were indistinguishable for three weeks. */}
          <Tile
            leading={iconAvatar(<SystemUpdateAltIcon />)}
            title="App updates"
            subtitle={`Version ${APP_VERSION || '—'} — check for a newer one`}
            onClick={() => setUpdateOpen(true)}
          />

          <Tile
            leading={iconAvatar(<InfoIcon />)}
            title="About Us"
            onClick={() => navigate('/about')}
          />
        </Box>
      </Box>

      {/* userScreenFunctions.dart promptForUniqueNumber() */}

      <Dialog open={testOpen} onClose={() => setTestOpen(false)}
        PaperProps={{ sx: { bgcolor: c.back, borderRadius: '20px', minWidth: 300 } }}>
        <DialogTitle sx={{ color: c.text, fontWeight: 700, textAlign: 'center' }}>
          Enter Unique Number
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth placeholder="Type here..."
            value={uno} onChange={(e) => setUno(e.target.value)}
            sx={{ mt: 1, '& .MuiInputBase-input': { color: c.text } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestOpen(false)} sx={{ color: c.text }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!uno.trim()}
            onClick={() => { setTestOpen(false); navigate(`/device/${uno.trim()}`); }}
            sx={{ bgcolor: c.button, color: c.text }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <AppUpdateDialog open={updateOpen} onClose={() => setUpdateOpen(false)} />
    </Box>
  );
}
